import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import Pagination from "../../components/admin/Pagination";
import RefreshButton from "../../components/admin/RefreshButton";
import { adminService } from "../../features/admin/service";
import type { SellerDeletedDeal, SellerDeletionStat } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";

/** Human-readable deal lifetime. This is the column that separates ordinary
 *  housekeeping from abuse: a deal that lived three weeks is a seller tidying
 *  up, a seller whose deals average four minutes is looping upload/delete to
 *  farm the AI pipeline. */
function formatLifetime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const m = mins % 60;
    return m ? `${hours}h ${m}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    // Show the leftover hours: a bare floor renders 2d21h as "2d", which
    // reads as nearly a full day shorter than the deal actually lived.
    const h = hours % 24;
    return h ? `${days}d ${h}h` : `${days}d`;
  }
  return `${Math.floor(days / 30)}mo`;
}

/** Under 15 minutes alive is the rough "worth a look" threshold — not a
 *  verdict, just what draws an admin's eye. Deliberately only ever styling,
 *  never an automatic block: a false positive that silently punishes a
 *  paying seller costs far more than a day's delay in someone reviewing it. */
const SUSPICIOUS_LIFETIME_SECONDS = 15 * 60;

const TH = "text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap";

/** Comma list capped to `max` entries with a "+N" remainder — same treatment
 *  the live Deals table gives destinations and tags, so wide rows stay
 *  readable. */
function ChipList({ values, max = 2 }: { values: string[]; max?: number }) {
  if (!values.length) return <span className="text-gray-600">—</span>;
  const shown = values.slice(0, max);
  const rest = values.length - shown.length;
  return (
    <span className="text-gray-300" title={values.join(", ")}>
      {shown.join(", ")}
      {rest > 0 && <span className="text-gray-500"> +{rest}</span>}
    </span>
  );
}

export default function AdminDeletedDealsPage() {
  const [rows, setRows] = useState<SellerDeletedDeal[]>([]);
  const [stats, setStats] = useState<SellerDeletionStat[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<SellerDeletedDeal | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.listSellerDeletedDeals({
        page, page_size: pageSize, search, seller_id: sellerFilter || undefined,
      });
      setRows(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch {
      showError("Couldn't load deleted deals.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sellerFilter, showError]);

  useEffect(() => { void load(); }, [load]);

  // Keep the page index inside the result set. Without this, filtering to a
  // seller with 1 deletion while on page 2 leaves the footer reading
  // "Showing 26-1 of 1" and an empty table — the request was for rows that
  // don't exist. Also covers deleting the last row on the final page.
  useEffect(() => {
    if (loading) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [total, pageSize, page, loading]);

  useEffect(() => {
    if (!showSummary) return;
    adminService
      .getSellerDeletionStats({ days: 30, limit: 50 })
      .then(setStats)
      .catch(() => showError("Couldn't load deletion stats."));
  }, [showSummary, showError]);

  const openDetail = async (deal: SellerDeletedDeal) => {
    setDetailLoading(true);
    setDetail({ id: deal.id, title: deal.title });
    try {
      setDetail(await adminService.getSellerDeletedDeal(deal.id));
    } catch {
      showError("Couldn't load the archived deal.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const purge = async (deal: SellerDeletedDeal) => {
    setBusyId(deal.id);
    try {
      await adminService.purgeSellerDeletedDeal(deal.id);
      showSuccess("Content and media destroyed. Tracking row kept.");
      setPurgeTarget(null);
      if (detail?.id === deal.id) setDetail(null);
      await load();
    } catch {
      showError("Couldn't permanently delete this deal.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Deleted by Seller"
        subtitle="Deals sellers removed themselves. None of these are live or indexed — but none are lost either: the full deal, media included, is archived here and recoverable. Nothing on this page expires or auto-deletes. An admin's own delete is permanent and never appears here."
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }}
          className="flex gap-2 flex-1 min-w-[260px]"
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search deal title or seller email…"
            className="flex-1 px-4 py-2 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
          <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-medium transition-colors">
            Search
          </button>
        </form>
        {sellerFilter && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-900/40 border border-indigo-800 rounded-xl text-xs text-indigo-300">
            Seller filtered
            <button onClick={() => { setSellerFilter(""); setPage(1); }} className="ml-0.5 hover:opacity-70 transition-opacity">×</button>
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowSummary((v) => !v)}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-xs font-medium transition-colors"
        >
          {showSummary ? "Hide" : "Show"} who deletes the most
        </button>
        <RefreshButton onRefresh={load} />
      </div>

      {showSummary && (
        <div className="mb-6 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-900 border-b border-gray-800 text-xs text-gray-400">
            One row per seller who deleted anything in the last 30 days, most deletions first.{" "}
            <span className="text-gray-300">Median lifetime</span> is the number to read: if a seller's deals
            typically lived weeks before deletion, that's normal tidying up. If they typically lived minutes,
            they may be uploading and deleting on a loop to farm free AI generations. Flagged rows are for a
            human to judge — nothing on this page blocks anyone automatically.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className={TH}>Seller</th>
                  <th className={TH}>Tier</th>
                  <th className={`${TH} text-right`}>Deletions</th>
                  <th className={`${TH} text-right`}>Were live</th>
                  <th className={`${TH} text-right`}>Median lifetime</th>
                  <th className={TH}>Last deletion</th>
                  <th className={`${TH} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!stats.length && (
                  <tr><td colSpan={7} className="py-10 text-center text-gray-500">No deletions in this window.</td></tr>
                )}
                {stats.map((s) => {
                  const suspicious =
                    s.median_lifetime_seconds !== null &&
                    s.median_lifetime_seconds < SUSPICIOUS_LIFETIME_SECONDS &&
                    s.deletions >= 5;
                  return (
                    <tr key={s.seller_id} className="border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors">
                      <td className="py-3 px-4 text-gray-200">{s.seller_email || s.seller_id}</td>
                      <td className="py-3 px-4 text-gray-400 capitalize">{s.tier || "—"}</td>
                      <td className="py-3 px-4 text-right text-gray-200">{s.deletions}</td>
                      <td className="py-3 px-4 text-right text-gray-400">{s.published_deletions}</td>
                      <td className={`py-3 px-4 text-right ${suspicious ? "text-amber-400 font-semibold" : "text-gray-400"}`}>
                        {formatLifetime(s.median_lifetime_seconds)}
                        {suspicious && <span className="ml-1" title="Short-lived deals — worth a look">⚠</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">
                        {s.last_deleted_at ? formatIST(s.last_deleted_at) : "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => { setSellerFilter(s.seller_id); setPage(1); setShowSummary(false); }}
                          className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors"
                        >
                          View deletions
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className={TH}>Title</th>
                <th className={TH}>Type</th>
                <th className={TH}>Category</th>
                <th className={TH}>Destination</th>
                <th className={TH}>Tags</th>
                <th className={TH}>Price</th>
                <th className={TH}>Seller</th>
                <th className={TH}>State when deleted</th>
                <th className={`${TH} text-right`}>Lifetime</th>
                <th className={TH}>Slot refunded</th>
                <th className={TH}>Created</th>
                <th className={TH}>Deleted</th>
                <th className={`${TH} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="py-20 text-center text-gray-500">Loading…</td></tr>
              ) : !rows.length ? (
                <tr><td colSpan={13} className="py-20 text-center text-gray-500">No seller-deleted deals yet</td></tr>
              ) : (
                rows.map((d) => {
                  const suspicious =
                    d.lifetime_seconds !== null && d.lifetime_seconds < SUSPICIOUS_LIFETIME_SECONDS;
                  const price = d.discounted_price || d.original_price;
                  return (
                    <tr key={d.id} className="group border-b border-gray-800/60 hover:bg-gray-900/40 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {d.image ? (
                            <img src={d.image} alt="" className="h-9 w-9 rounded-md object-cover shrink-0" />
                          ) : (
                            <div className="h-9 w-9 rounded-md bg-gray-800 shrink-0" />
                          )}
                          <button
                            onClick={() => void openDetail(d)}
                            className="text-gray-200 truncate max-w-[240px] text-left hover:text-indigo-400 transition-colors"
                            title={d.title}
                          >
                            {d.title}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{d.source_collection || "—"}</td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{d.primary_slug || "—"}</td>
                      <td className="py-3 px-4 max-w-[180px]">
                        <ChipList values={[...d.cities, ...d.countries]} />
                      </td>
                      <td className="py-3 px-4 max-w-[160px]"><ChipList values={d.tags} /></td>
                      <td className="py-3 px-4 text-gray-300 whitespace-nowrap">
                        {price ? `${d.currency} ${price.toLocaleString()}` : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => { setSellerFilter(d.seller_id); setPage(1); }}
                          className="text-gray-300 hover:text-indigo-400 transition-colors text-left"
                          title="Filter to this seller's deletions"
                        >
                          {d.seller_email || d.seller_name || d.seller_id}
                        </button>
                        {d.tier_at_time && (
                          <div className="text-xs text-gray-600 capitalize">{d.tier_at_time}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={d.was_published ? "text-emerald-400" : "text-gray-500"}>
                          {d.was_published ? "Was live" : "Draft"}
                        </span>
                        {d.had_boost && (
                          <span className="ml-1.5 text-amber-400 text-xs" title="Had a boost campaign">boosted</span>
                        )}
                      </td>
                      <td className={`py-3 px-4 text-right whitespace-nowrap ${suspicious ? "text-amber-400 font-semibold" : "text-gray-400"}`}>
                        {formatLifetime(d.lifetime_seconds)}
                        {suspicious && <span className="ml-1" title="Very short-lived — worth a look">⚠</span>}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {d.refunded ? (
                          <span className="text-emerald-400" title="The seller got their monthly upload slot back">
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-500" title={d.refund_label || "No refund recorded"}>
                            No
                            {d.refund_label && (
                              <span className="block text-[11px] text-gray-600">{d.refund_label}</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                        {d.deal_created_at ? formatIST(d.deal_created_at) : "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">
                        {d.deleted_at ? formatIST(d.deleted_at) : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => void openDetail(d)}
                            className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors"
                          >
                            View
                          </button>
                          {d.content_purged ? (
                            <span className="px-2 py-1 text-xs text-gray-500" title="Content and media destroyed; tracking row kept">
                              Content purged
                            </span>
                          ) : (
                            <button
                              onClick={() => setPurgeTarget(d)}
                              disabled={busyId === d.id}
                              className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              Purge content
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page - 1}
        pageSize={pageSize}
        total={total}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
          <div className="w-full max-w-3xl my-8 bg-gray-900 border border-gray-800 rounded-2xl">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-start justify-between rounded-t-2xl">
              <div className="min-w-0">
                <h3 className="text-white font-semibold truncate">{detail.title || "Archived deal"}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Archived copy — not live, not indexed, fully recoverable.
                </p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-gray-300 text-2xl leading-none shrink-0 ml-4">×</button>
            </div>
            <div className="p-6">
              {detailLoading ? (
                <div className="py-16 text-center text-gray-500">Loading…</div>
              ) : (
                <pre className="text-xs text-gray-300 bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-[60vh] overflow-y-auto">
                  {JSON.stringify(detail, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {purgeTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-2">Purge this deal's content?</h3>
            <p className="text-sm text-gray-400 mb-4">
              <span className="text-gray-200">{purgeTarget.title}</span> — its images, media and full
              document will be destroyed for good. Right now the deal is only archived and could still
              be recovered; this removes that possibility.
            </p>
            <p className="text-sm text-gray-400 mb-4">
              The <span className="text-gray-200">tracking row stays</span> — seller, dates, lifetime
              and refund outcome are kept, so this seller's deletion history remains complete and their
              churn stays measurable.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setPurgeTarget(null)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-xs font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void purge(purgeTarget)}
                disabled={busyId === purgeTarget.id}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-xl text-xs font-semibold disabled:opacity-50"
              >
                {busyId === purgeTarget.id ? "Purging…" : "Purge content"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
